import client from '../client';
import type { ApiResponse } from '../../types/api';

/** A category node from the source store (flattened, with depth + parentId). */
export interface ImportCategory {
  id: string;
  slug: string | null;
  name: string;
  image: string | null;
  depth: number;
  parentId: string | null;
}

/** A deduped product in the crawl preview. */
export interface ImportProductPreview {
  sourceId: string;
  title: string;
  description: string;
  shortDescription: string | null;
  price: number;
  salePrice: number | null;
  productType: string;
  sku: string | null;
  quantity: number | string;
  currency: string;
  imageUrls: string[];
  url: string | null;
  promotionTitle: string | null;
  available: boolean;
  categoryIds: string[];
  conflict: null | 'sku' | 'title';
  // Variants + custom input fields (populated only for products that have them).
  hasOptions: boolean;
  hasVariants: boolean;
  variantTypes: { id: string; name: string; displayType: string; options: { id: string; name: string; colorHex?: string }[] }[];
  variants: { id: string; combination: Record<string, string>; quantity?: number; priceModifier?: number; isActive: boolean; isDefault?: boolean }[];
  fields: { id: number; title: string; fieldType: string; requirement: string }[];
  pricingWarning: boolean;
}

export interface CrawlProgress {
  totalCategories: number;
  doneCategories: number;
  discovered: number;
  perCategory: Record<string, { name: string; state: 'pending' | 'fetching' | 'done' | 'error'; count: number }>;
  recent: string[];
  enrichTotal?: number;
  enrichDone?: number;
}

export interface ImportStats {
  total: number;
  created: number;
  skipped: number;
  failed: number;
  imageOk: number;
  imageFail: number;
  recent: string[];
}

export type ImportStatus = 'inspected' | 'crawling' | 'ready' | 'importing' | 'done' | 'failed';

export interface ImportJob {
  importId: string;
  status: ImportStatus;
  store: { name: string; currency: string };
  categories: ImportCategory[];
  selectedCategoryIds: string[];
  crawl: CrawlProgress | null;
  products: ImportProductPreview[];
  conflictSummary: { sku: number; title: number };
  importStats: ImportStats | null;
  error: string | null;
}

interface InspectResult {
  importId: string;
  store: { name: string; platform: string; currency: string; logo: string | null };
  categories: ImportCategory[];
}

export const productImportApi = {
  /** Step 1: resolve a store URL and fetch its category tree. */
  inspect: async (storeId: string, storeUrl: string): Promise<InspectResult> => {
    const res = await client.post<ApiResponse<InspectResult>>(
      `/dashboard/${storeId}/products/import/inspect`,
      { storeUrl },
    );
    return res.data.data!;
  },

  /** Step 2: start the background crawl of selected categories. */
  crawl: async (storeId: string, importId: string, categoryIds: string[]) => {
    const res = await client.post<ApiResponse<{ ok: boolean; selected: number }>>(
      `/dashboard/${storeId}/products/import/${importId}/crawl`,
      { categoryIds },
    );
    return res.data.data!;
  },

  /** Step 3: poll job state. */
  status: async (storeId: string, importId: string): Promise<ImportJob> => {
    const res = await client.get<ApiResponse<ImportJob>>(
      `/dashboard/${storeId}/products/import/${importId}`,
    );
    return res.data.data!;
  },

  /** Step 4: start the background import of chosen products. */
  run: async (
    storeId: string,
    importId: string,
    sourceIds: string[],
    conflictPolicy: 'skip' | 'create',
    parentName?: string,
  ) => {
    const res = await client.post<ApiResponse<{ ok: boolean; queued: number }>>(
      `/dashboard/${storeId}/products/import/${importId}/run`,
      { sourceIds, conflictPolicy, parentName: parentName || undefined },
    );
    return res.data.data!;
  },
};
